import {assertNotNull, safeText} from "../../js/util/CommonUtils.js";

export default class FileDropAreaHtml {
	#root = null;
	#onEnterCallbacks = [];
	#onLeaveCallbacks = [];
	/** @type {Function[]} */
	#onDropCallbacks = [];
	#isMouseWithinArea = false;

	/**
	 * @param {HTMLElement} element
	 * @param {{svgIcon?: string, message?:string, showOverlay?:boolean}} props
	 */
	init(element, {svgIcon, message, showOverlay = true}) {
		this.#root = assertNotNull(element);
		element.addEventListener('dragover', (evt) => {
			evt.preventDefault();
			evt.stopPropagation()
		}, true);
		element.addEventListener('dragenter', (evt) => {
			evt.preventDefault();
			if (!this.#isMouseWithinArea) {
				this.#isMouseWithinArea = true;
				if (showOverlay)
					this._showOverlay(
						message || FileDropAreaHtml.#DEFAULT_MESSAGE,
						svgIcon || FileDropAreaHtml.#DEFAULT_ICON
					);
				for (const onEnterCallback of this.#onEnterCallbacks)
					onEnterCallback();
			}
		}, true);
		element.addEventListener('dragleave', (evt) => {
			if (!element.contains(evt.relatedTarget)){
				this.#isMouseWithinArea = false;
				if (showOverlay)
					this._removeOverlay();
				for (const onLeaveCallback of this.#onLeaveCallbacks)
					onLeaveCallback();
			}
		}, true);
		element.addEventListener('drop', async (evt) => {
			evt.preventDefault();
			evt.stopPropagation();
			if (showOverlay)
				this._removeOverlay();
			if (this._isDragFiles(evt))
				for (const cb of this.#onDropCallbacks)
					cb(evt.dataTransfer.files);
		}, true);
	}

	onDragEnter(cb) {
		this.#onEnterCallbacks.push(cb);
	}

	onDragLeave(cb) {
		this.#onLeaveCallbacks.push(cb);
	}

	/**
	 * @param {Function} cb - the callback should accept a {@link FileList}
	 */
	onDrop(cb) {
		this.#onDropCallbacks.push(cb);
	}

	/**
	 * Browsers allow dragging elements like {@code <a>} and {@code <img>}, we don't want to think user uploads
	 * a file in this cases.
	 *
	 * @param {DragEvent} evt
	 * @private
	 */
	_isDragFiles(evt) {
		// Seems like the file list is present only when dropping and is always empty when we're dragging.
		// So doesn't make sense to add this check to any handler by for the 'drop' event.
		return evt.dataTransfer.files.length > 0;
	}

	/**
	 * @param {string} message
	 * @param {string} icon
	 * @private
	 */
	_showOverlay(message, icon) {
		this.#root.insertAdjacentHTML("beforeend", this._htmlTemplate(message, icon));
	}

	_removeOverlay() {
		this.#root.querySelector("." + FileDropAreaHtml.#OVERLAY_CSS_CLASS).remove();
	}

	/**
	 * @param {string} message
	 * @param {string} icon
	 * @returns {string}
	 * @private
	 */
	_htmlTemplate(message, icon) {
		return `<div class="${FileDropAreaHtml.#OVERLAY_CSS_CLASS}">
                	${icon}
                	<span>${safeText(message)}</span>
              </div>`
	}

	/**
	 * For testing
	 * @private
	 */
	_getOverlayElement(){
		return this.#root.querySelector('.' + FileDropAreaHtml.#OVERLAY_CSS_CLASS);
	}


	static #DEFAULT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="128px" height="128px" viewBox="0 0 128 128">
          			  			<path 	d="M91.215,90H77c-0.553,0-1-0.447-1-1s0.447-1,1-1h14.215C103.431,88,113,78.684,113, 66.79c0-11.908-9.772-21.596-21.785-21.596c-1.498,0-3.062,0.188-4.785,0.575c-0.418, 0.094-0.842-0.086-1.066-0.443c-5.187-8.244-14.08-13.166-23.789-13.166c-13.957,0-25.628, 10.019-27.751, 23.823c-0.083,0.542-0.584,0.928-1.132, 0.837c-0.82-0.119-1.551-0.177-2.235-0.177C21.789,56.644,15,63.323,15,71.852C15,80.604,22.078, 88,30.456,88H52c0.553,0,1,0.447,1,1s-0.447,1-1,1H30.456C20.994,90,13,81.689,13,71.852c0-9.649, 7.668-17.208,17.456-17.208c0.496,0,1.01,0.026,1.553,0.079C34.664,40.432,46.947,30.16, 61.574, 30.16c10.167,0,19.499,5.038,25.103,13.513c1.604-0.321,3.095-0.479,4.538-0.479C104.33,43.194,115, 53.779,115,66.79C115,79.805,104.553,90,91.215,90z"
          			  	 				fill="#323638"/>
              		  			<path 	d="M64.589,97.825c-0.553,0-1-0.447-1-1V62.223L51.992,74.835c-0.374,0.408-1.006,0.434-1.413, 0.06c-0.406-0.374-0.434-1.007-0.06-1.413l13.333-14.5c0.279-0.304,0.716-0.404,1.1-0.255s0.637,0.52,0.637, 0.932v37.167C65.589,97.378,65.142,97.825,64.589,97.825z"
              		  	 				fill="#323638"/>
              		  	 		<path 	d="M77.922,75.325c-0.271,0-0.543-0.11-0.74-0.327L63.849,60.331c-0.371-0.409-0.341-1.041, 0.067-1.413c0.408-0.37,1.042-0.341,1.413,0.067l13.333,14.667c0.371,0.409,0.341,1.041-0.067, 1.413C78.403,75.239,78.162,75.325,77.922,75.325z"
              		  	 				 fill="#323638"/>
              				</svg>`;
	static #DEFAULT_MESSAGE = "Drop or paste here";
	static #OVERLAY_CSS_CLASS = "drag-n-drop-area";
}